import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Bike, Clock, MapPin, Navigation, Phone, Users } from 'lucide-react';
import { PageLayout } from '../components/global/PageLayout';
import { Section } from '../components/ui/PageIntro';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { ImageBox } from '../components/ui/ImageBox';
import { EventCard } from '../components/LowOnFuel/EventCard';
import { BookingForm } from '../components/LowOnFuel/BookingForm';
import { getEvent, getPublishedEvents } from '../data/events';
import { CONTACT, DEFAULT_OG_IMAGE } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { NotFound } from './NotFound';
export function EventDetail() {
  const {
    slug
  } = useParams();
  // Unpublished events resolve to undefined and 404. See D41.
  const event = getEvent(slug ?? '');
  if (!event) return <NotFound data-id="element-490" data-mp="316q7swad7h8k" />;
  return <EventDetailView key={event.id} eventId={event.id} data-id="element-491" data-mp="3155etb3p86ct" />;
}

/**
 * Split out so the metadata hook is never called conditionally, and so
 * navigating between two events remounts rather than reusing the previous
 * event's form state.
 */
function EventDetailView({
  eventId
}: {
  eventId: string;
}) {
  const event = getEvent(eventId)!;
  useDocumentMeta({
    title: `${event.title} | Low On Fuel`,
    description: event.blurb,
    path: `/whats-on/${event.id}`,
    ogImage: event.image || DEFAULT_OG_IMAGE.src,
    ogImageAlt: event.imageAlt || DEFAULT_OG_IMAGE.alt
  });
  const isWalkIn = event.booking === 'walk-in';
  const isSoldOut = !isWalkIn && event.spotsLeft === 0;
  /**
   * `capacity` and `spotsLeft` are optional (D46), so the count is only
   * rendered when BOTH are genuinely present and not already full.
   * `null` means "we have spaces but no number to quote", which is a true
   * statement; the previous code interpolated the raw fields and could
   * have printed "undefined of undefined left".
   */
  const knownCount = event.spotsLeft !== undefined && event.capacity !== undefined && event.spotsLeft !== event.capacity ? `${event.spotsLeft} of ${event.capacity} left` : null;
  const isCommunity = event.type === 'community';
  const related = getPublishedEvents().filter(candidate => candidate.id !== event.id).slice(0, 3);
  return <PageLayout data-id="element-492" data-mp="29za5dg90fau0">
      {/* Photographic head, with the title over the image as on the index
          pages. The difference is that a single event has a real ancestor
          trail to show. */}
      <div className="relative isolate bg-ink-deep" data-id="element-493" data-mp="1hcpfkk86n6sk">
        <img src={event.image} alt="" aria-hidden="true" loading="eager" decoding="sync" {...{
        fetchpriority: 'high'
      }} className="absolute inset-0 -z-10 h-full w-full object-cover" data-id="element-494" data-mp="1h9ba33482hgw" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink-deep via-ink-deep/80 to-ink-deep/45" aria-hidden="true" data-id="element-495" data-mp="tlbjq4atot2u" />

        <div className="mx-auto flex min-h-[16rem] max-w-container flex-col justify-end px-gutter-sm py-10 sm:min-h-[20rem] sm:px-gutter-md sm:py-12 lg:px-gutter-lg" data-id="element-496" data-mp="r26vwfi30fav">
          <div className="mb-5" data-id="element-497" data-mp="m0hm136li7od">
            <Breadcrumbs items={[{
            label: 'Home',
            to: '/'
          }, {
            label: "What's on",
            to: '/whats-on'
          }, {
            label: event.title
          }]} onDark data-id="element-498" data-mp="h2y3vs6s1ar5" />
          </div>
          <p className="mb-3 border-l-4 border-flame pl-3 text-xs font-black uppercase tracking-[0.22em] text-flame sm:text-sm" data-id="element-499" data-mp="84vx0w0d3nvy">
            {event.when}
          </p>
          <h1 className="font-bebas text-5xl leading-[0.88] tracking-wide text-cream sm:text-6xl lg:text-7xl" data-id="element-500" data-mp="3uj5jv0on8num">
            {event.title}
          </h1>
        </div>
      </div>

      {/* Facts band: the four things someone checks before reading a word. */}
      <div className="border-b border-hairline bg-cream" data-id="element-501" data-mp="3gimha92y69zu">
        <div className="mx-auto max-w-container px-gutter-sm py-6 sm:px-gutter-md lg:px-gutter-lg" data-id="element-502" data-mp="3gj4g9dgzv2bg">
          <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-503" data-mp="1kg9ko21m1nf1">
            <div className="flex items-start gap-2.5" data-id="element-504" data-mp="1og06drca5dcp">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-moss" aria-hidden="true" data-id="element-505" data-mp="1oiw4bfo4o215" />
              <div data-id="element-506" data-mp="1ankarblpkhlt">
                <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink/50" data-id="element-507" data-mp="1forft2wgv2os">
                  Where
                </dt>
                <dd className="text-sm font-medium text-ink" data-id="element-508" data-mp="10qfjwygx3xyo">{event.location}</dd>
              </div>
            </div>

            <div className="flex items-start gap-2.5" data-id="element-509" data-mp="15s4yup79gvs1">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-moss" aria-hidden="true" data-id="element-510" data-mp="rrc1zxthlolr" />
              <div data-id="element-511" data-mp="wplxk9j4ir7w">
                <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-512" data-mp="wp3uou1yher0">
                  How long
                </dt>
                <dd className="text-sm font-medium text-ink" data-id="element-513" data-mp="2m5lb02trz2tc">{event.duration}</dd>
              </div>
            </div>

            <div className="flex items-start gap-2.5" data-id="element-514" data-mp="2r1k7yet65t5d">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-moss" aria-hidden="true" data-id="element-515" data-mp="318mvd7tjhpre" />
              <div data-id="element-516" data-mp="2w9tfx9uozool">
                <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-517" data-mp="2wablwy75ph9p">
                  {isWalkIn ? 'Space' : 'Availability'}
                </dt>
                {/* A walk-in event publishes no capacity number (D46), so
                    this answers the question someone riding over actually
                    has: will there be room to put the bike? */}
                <dd className="flex items-center gap-1.5 text-sm font-bold text-ink" data-id="element-518" data-mp="3eyghhidntvxf">
                  <span aria-hidden="true" className={isSoldOut ? 'text-rust' : isWalkIn || knownCount === null ? 'text-moss' : 'text-flame'} data-id="element-519" data-mp="3sxzq0cadv9a9">
                    {isSoldOut ? '■' : isWalkIn || knownCount === null ? '●' : '▲'}
                  </span>
                  {isSoldOut ? 'Fully booked' : isWalkIn ? event.spaceNote ?? 'Walk in, no booking' : knownCount === null ? 'Spaces available' : knownCount}
                </dd>
              </div>
            </div>

            <div className="flex items-start gap-2.5" data-id="element-520" data-mp="gglck96i7gck">
              <span className="mt-0.5 w-4 shrink-0 text-center text-sm font-black text-moss" aria-hidden="true" data-id="element-521" data-mp="lgpt1z7ewb2s">
                £
              </span>
              <div data-id="element-522" data-mp="v6j48z3n6o6i">
                <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-523" data-mp="v72bnlvo1u6a">
                  Price
                </dt>
                <dd className="text-sm font-medium text-ink" data-id="element-524" data-mp="1076tbf8b8uy3">
                  {event.price ? `£${event.price} per rider` : 'Free to attend'}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>

      <Section className="pb-8 sm:pb-10" data-id="element-525" data-mp="2kno33krt5mo3">
        <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-14" data-id="element-526" data-mp="2kmlbtbqkpi7n">
          <div data-id="element-527" data-mp="2fo1pha82i4er">
            <h2 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-528" data-mp="2fpml7364bte7">
              About this event
            </h2>
            <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-529" data-mp="2arb9so06datv">
              {event.description}
            </p>

            {isCommunity && <p className="mt-6 max-w-prose border-l-4 border-moss bg-moss/10 px-4 py-3 leading-relaxed text-ink" data-id="element-530" data-mp="33dw4nyfv0526">
                <strong className="font-bold" data-id="element-531" data-mp="2ym4jk3fh2rjf">Free event.</strong> Donations
                are welcome and go to the volunteer mechanics who run the
                night.
              </p>}

            {/* On a page of its own the route is simply shown. It was folded
                into a disclosure only because a dialog had no room for it. */}
            {event.route && <div className="mt-10" data-id="element-532" data-mp="2uongs6zjug5l">
                <h2 className="flex items-center gap-2.5 font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-533" data-mp="2unm4y0aplpmt">
                  <Navigation className="h-6 w-6 text-moss" aria-hidden="true" data-id="element-534" data-mp="2loqv8e8ghxer" />
                  The route
                </h2>
                <p className="mt-3 text-ink/80" data-id="element-535" data-mp="2ppkj57lwfc7n">
                  {event.route.distance} · {event.route.estimatedTime}{' '}
                  including breaks
                </p>

                <ol className="mt-6 space-y-4" data-id="element-536" data-mp="3ifjflo2fabmc">
                  {event.route.stops.map((stop, index) => <li key={`${stop.name}-${index}`} className="flex items-start gap-4" data-id="element-537" data-mp="34g1miwi3gc76" data-mpi={index}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-moss text-sm font-black text-white" aria-hidden="true" data-id="element-538" data-mp="39g64kqynhezi">
                        {index + 1}
                      </span>
                      <span data-id="element-539" data-mp="1pk8nm01k4pz3">
                        <span className="block font-bold text-ink" data-id="element-540" data-mp="1lgu6f61vu3k2">
                          {stop.name}
                        </span>
                        <span className="block text-sm text-ink-muted" data-id="element-541" data-mp="1zf0qnjdws519">
                          {stop.detail}
                        </span>
                      </span>
                    </li>)}
                </ol>

                <p className="mt-5 text-sm text-ink-muted" data-id="element-542" data-mp="16pbjspeiparq">
                  GPX and TomTom route files are sent with your booking
                  confirmation.
                </p>
              </div>}

            <div className="mt-10 overflow-hidden rounded-container border border-hairline" data-id="element-543" data-mp="1br0ziicoqcm5">
              <ImageBox src={event.image} alt={event.imageAlt} ratio="16/9" data-id="element-544" data-mp="o16lvykjkk0u" />
            </div>
          </div>

          {/* Action column. Static: a booking form that tracks the viewport
              while you are trying to read the route below it is harder to
              use, not easier. */}
          <div data-id="element-545" data-mp="35mjrh25ylxmg">
            {isWalkIn ? <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-546" data-mp="35nuxz9qshjh9">
                <h2 className="flex items-center gap-2.5 font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-547" data-mp="30mfdbisixeze">
                  <Bike className="h-6 w-6 text-flame" aria-hidden="true" data-id="element-548" data-mp="2vnu8f62bu74m" />
                  Just turn up
                </h2>
                <p className="mt-3 leading-relaxed text-ink/75" data-id="element-549" data-mp="2rolhsa2l2pda">
                  No booking needed and no cover charge. There's plenty of
                  room in the car park, so park up, get a drink and find a
                  spot. Come a bit earlier if you'd like to be out on the
                  deck.
                </p>
                <a href={CONTACT.telephoneHref} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-container border border-control px-5 py-3.5 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-550" data-mp="27zb0wsr3pydl">
                  <Phone className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-551" data-mp="sftwevwmq5dn" />
                  Ring us on {CONTACT.telephone}
                </a>
              </div> : isSoldOut ? <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-552" data-mp="3vwk9fuqmpiou">
                <p className="flex items-center gap-2 font-bold text-rust" data-id="element-553" data-mp="3vupmkavsrgi4">
                  <span aria-hidden="true" data-id="element-554" data-mp="4qis54g2lsur">■</span>
                  This one is fully booked
                </p>
                <p className="mt-2 leading-relaxed text-ink/75" data-id="element-555" data-mp="9n9bq1r336kx">
                  Give us a ring to go on the cancellation list, or have a
                  look at what else is coming up.
                </p>
                <a href={CONTACT.telephoneHref} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-container bg-flame px-5 py-3.5 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-556" data-mp="3i55p9kkzjbg7">
                  <Phone className="h-4 w-4" aria-hidden="true" data-id="element-557" data-mp="3ev6bmrktwsgl" />
                  {CONTACT.telephone}
                </a>
                <Link to="/whats-on" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-container border border-control px-5 py-3.5 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-558" data-mp="2r5296njdmyr6">
                  See what else is on
                </Link>
              </div> : <BookingForm event={event} data-id="element-559" data-mp="gfdgs5bua96w" />}
          </div>
        </div>

        <div className="mt-12 border-t border-hairline pt-8" data-id="element-560" data-mp="be7k5wf261s3">
          <Link to="/whats-on" className="inline-flex items-center gap-2 rounded-container border border-control px-5 py-3 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-561" data-mp="1tb18f5q6oabg">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-562" data-mp="1pbaeit5d1e2p" />
            All events
          </Link>
        </div>
      </Section>

      {related.length > 0 && <div className="border-t border-hairline bg-cream" data-id="element-563" data-mp="2h26mbfckcsj4">
          <Section labelledBy="related-heading" data-id="element-564" data-mp="2h3rgh3uyu9gg">
            <h2 id="related-heading" className="font-bebas text-4xl leading-none tracking-wide text-ink" data-id="element-565" data-mp="23b1taw3payhr">
              Also coming up
            </h2>
            <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-566" data-mp="2vyny27oo0967">
              {related.map(candidate => <li key={candidate.id} className="flex" data-id="element-567" data-mp="30qfqknrdsoyz">
                  <div className="flex-1" data-id="element-568" data-mp="1c3l8a6ehxtys">
                    <EventCard event={candidate} data-id="element-569" data-mp="1c4mm2imwcuk7" />
                  </div>
                </li>)}
            </ul>
          </Section>
        </div>}
    </PageLayout>;
}